import React from "react";
import { Easing, Platform, Pressable, StyleSheet, View, type ViewStyle } from "react-native";
import { MotiView } from "moti";
import { createPortal } from "react-dom";
import { ArrowSquareOutIcon, CheckIcon, PencilSimpleIcon, StackIcon, XIcon } from "@phosphor-icons/react";

import CustomText from "../../../../../components/common/general/CustomText/CustomText";
import GapActionDrawerLayout from "../../../../../components/adminDashboard/CourseModernizer/GapActionDrawerLayout";
import { colors } from "../../../../../styles/design-system";
import type { CourseEvaluatorReviewData } from "../types/reviewTypes";
import GapAnalysisDimensionRail from "./GapAnalysisDimensionRail";
import type { GapAnalysisDimension } from "./gapAnalysisConstants";
import {
  COURSE_OVERVIEW_DRAWER_CONTENT,
  DRAWER_FOOTER_CONTENT,
  type GapActionFlagPillData,
} from "./gapActionDrawerConstants";

export type GapActionDrawerVariant = "course-overview" | "gap-analysis-scorecard";

interface GapActionDrawerSlideoutProps {
  review: CourseEvaluatorReviewData;
  dimensions?: GapAnalysisDimension[];
  variant: GapActionDrawerVariant;
  selectedDimensionId: string | null;
  onSelectDimension: (dimensionId: string) => void;
  onShowGapAnalysisScorecard: (dimensionId: string) => void;
  onBack: () => void;
  onClose: () => void;
  onAddToQueue?: () => void;
  onSkip?: () => void;
  onJumpToEntity?: (entityId: string, dimension: string) => void;
  onEdit?: (entityId: string, entityType: string) => void;
}

function GapActionDrawerSlideout({
  review,
  dimensions,
  variant,
  selectedDimensionId,
  onSelectDimension,
  onShowGapAnalysisScorecard,
  onBack,
  onClose,
  onAddToQueue,
  onSkip,
  onJumpToEntity,
  onEdit,
}: GapActionDrawerSlideoutProps) {
  const drawer = (
    <GapActionDrawer
      review={review}
      dimensions={dimensions}
      variant={variant}
      selectedDimensionId={selectedDimensionId}
      onSelectDimension={onSelectDimension}
      onShowGapAnalysisScorecard={onShowGapAnalysisScorecard}
      onBack={onBack}
      onClose={onClose}
      onAddToQueue={onAddToQueue}
      onSkip={onSkip}
      onJumpToEntity={onJumpToEntity}
      onEdit={onEdit}
    />
  );

  if (Platform.OS === "web" && typeof document !== "undefined") {
    return createPortal(drawer, document.body);
  }

  return drawer;
}

function GapActionDrawer({
  review,
  dimensions,
  variant,
  selectedDimensionId,
  onSelectDimension,
  onShowGapAnalysisScorecard,
  onBack,
  onClose,
  onAddToQueue,
  onSkip,
  onJumpToEntity,
  onEdit,
}: GapActionDrawerSlideoutProps) {
  if (variant === "gap-analysis-scorecard") {
    return (
      <DrawerShell title="Gap analysis" accessibilityLabel="Gap analysis dimensions" onBack={onBack} onClose={onClose}>
        <GapAnalysisDimensionRail
          review={review}
          dimensions={dimensions}
          selectedDimensionId={selectedDimensionId}
          onSelectDimension={onSelectDimension}
          onJumpToEntity={onJumpToEntity}
        />
      </DrawerShell>
    );
  }

  return (
    <CourseOverviewDrawer
      onShowGapAnalysisScorecard={onShowGapAnalysisScorecard}
      onBack={onBack}
      onClose={onClose}
      onAddToQueue={onAddToQueue}
      onSkip={onSkip}
      onEdit={onEdit}
    />
  );
}

function DrawerShell({
  title,
  accessibilityLabel,
  children,
  footer,
  onBack,
  onClose,
}: {
  title: string;
  accessibilityLabel: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  onBack: () => void;
  onClose: () => void;
}) {
  return (
    <MotiView
      from={{ translateX: 720 }}
      animate={{ translateX: 0 }}
      exit={{ translateX: 720 }}
      transition={{ type: "timing", duration: 500, easing: Easing.out(Easing.ease) }}
      exitTransition={{ type: "timing", duration: 300, easing: Easing.in(Easing.ease) }}
      style={styles.drawerMotion}
      role="dialog"
      accessibilityLabel={accessibilityLabel}>
      <GapActionDrawerLayout
        title={title}
        accessibilityLabel={accessibilityLabel}
        onBack={onBack}
        onClose={onClose}
        borderRadius={0}
        showCloseButton={true}
        containerStyle={styles.drawerLayout}
        footer={footer}>
        {children}
      </GapActionDrawerLayout>
    </MotiView>
  );
}

function CourseOverviewDrawer({
  onShowGapAnalysisScorecard,
  onBack,
  onClose,
  onAddToQueue,
  onSkip,
  onEdit,
}: Pick<
  GapActionDrawerSlideoutProps,
  "onShowGapAnalysisScorecard" | "onBack" | "onClose" | "onAddToQueue" | "onSkip" | "onEdit"
>) {
  const content = COURSE_OVERVIEW_DRAWER_CONTENT;

  const handleEdit = () => {
    onEdit?.("demo-course-overview-id", "module");
  };

  return (
    <DrawerShell
      title={content.title}
      accessibilityLabel={content.accessibilityLabel}
      onBack={onBack}
      onClose={onClose}
      footer={
        <DrawerFooter
          openEditorLabel={content.openEditorLabel}
          queueLabel={content.queueLabel}
          onAddToQueue={onAddToQueue}
          onSkip={onSkip}
        />
      }>
      <View style={styles.drawerBody}>
        <View style={styles.flaggedRow}>
          <CustomText
            text={content.flaggedInLabel}
            useTranslationText={false}
            size="xs"
            weight="bold"
            style={styles.flaggedLabel}
          />
          {content.flaggedPills.map((pill) => (
            <FlagPill key={pill.dimensionId} pill={pill} onShowGapAnalysisScorecard={onShowGapAnalysisScorecard} />
          ))}
        </View>

        <View style={styles.moduleBadge}>
          <StackIcon size={14} color="#7244BB" weight="bold" />
          <CustomText
            text={content.moduleBadgeLabel}
            useTranslationText={false}
            size="xs"
            weight="medium"
            style={styles.moduleBadgeText}
          />
        </View>

        <CustomText
          text={content.actionTitle}
          useTranslationText={false}
          size="xxl"
          weight="bold"
          style={styles.actionTitle}
        />

        <View style={styles.reasonCard}>
          <CustomText
            text={content.reasonTitle}
            useTranslationText={false}
            size="s"
            weight="bold"
            style={styles.reasonTitle}
          />
          <CustomText
            text={content.reasonText}
            useTranslationText={false}
            size="s"
            weight="normal"
            style={styles.reasonText}
          />
        </View>

        <View style={styles.diffCard}>
          <View style={styles.diffHeaderRow}>
            <View style={[styles.diffHeaderCell, styles.diffLeftBorder]}>
              <CustomText
                text={content.currentHeader}
                useTranslationText={false}
                size="s"
                weight="bold"
                style={styles.diffHeaderText}
              />
            </View>
            <View style={[styles.diffHeaderCell, styles.diffProposedHeader]}>
              <CustomText
                text={content.proposedHeader}
                useTranslationText={false}
                size="s"
                weight="bold"
                style={styles.proposedHeaderText}
              />
              <Pressable
                accessibilityRole="button"
                accessibilityLabel={content.editAccessibilityLabel}
                style={styles.editButton}
                onPress={handleEdit}>
                <PencilSimpleIcon size={14} color={colors.grey[700]} />
                <CustomText
                  text={content.editLabel}
                  useTranslationText={false}
                  size="xs"
                  weight="medium"
                  style={styles.editButtonText}
                />
              </Pressable>
            </View>
          </View>

          {content.diffRows.map((row) => (
            <DiffRow
              key={row.label}
              label={row.label}
              current={row.current}
              proposed={row.proposed}
              proposedMeta={row.proposedMeta}
              isChanged={row.isChanged}
            />
          ))}
        </View>
      </View>
    </DrawerShell>
  );
}

function DrawerFooter({
  queueLabel,
  openEditorLabel,
  onAddToQueue,
  onSkip,
}: {
  queueLabel: string;
  openEditorLabel: string;
  onAddToQueue?: () => void;
  onSkip?: () => void;
}) {
  const [isQueued, setIsQueued] = React.useState(false);
  const [isSkipped, setIsSkipped] = React.useState(false);

  const handleAddToQueue = () => {
    setIsQueued(!isQueued);
    setIsSkipped(false);
    onAddToQueue?.();
  };

  const handleRemoveFromQueue = () => {
    setIsQueued(false);
    setIsSkipped(false);
    onAddToQueue?.(); // Call the same handler to undo
  };

  const handleSkipRestore = () => {
    setIsSkipped(!isSkipped);
    setIsQueued(false);
    onSkip?.();
  };

  return (
    <View style={styles.drawerFooter}>
      <View style={styles.footerActions}>
        {/* When skipped, show "Skipped" status, when queued show "In Queue" status, otherwise show "Add to Queue" button */}
        {isSkipped ? (
          <View style={[styles.skipActionButton, styles.skipButtonSkipped]}>
            <XIcon size={15} color={colors.grey[900]} />
            <CustomText
              text="Skipped"
              useTranslationText={false}
              size="xs"
              weight="medium"
              style={styles.skipActionTextSkipped}
            />
          </View>
        ) : isQueued ? (
          <View style={[styles.queueButton, styles.queueButtonQueued]}>
            <CheckIcon size={15} color={colors.white} weight="bold" />
            <CustomText
              text="In Queue"
              useTranslationText={false}
              size="xs"
              weight="medium"
              style={styles.queueButtonText}
            />
          </View>
        ) : (
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={queueLabel}
            onPress={handleAddToQueue}
            style={styles.queueButton}>
            <CheckIcon size={15} color={colors.white} weight="bold" />
            <CustomText
              text={DRAWER_FOOTER_CONTENT.addToQueueLabel}
              useTranslationText={false}
              size="xs"
              weight="medium"
              style={styles.queueButtonText}
            />
          </Pressable>
        )}

        {/* When queued, show "Remove from Queue", when skipped show "Restore", otherwise show "Skip" */}
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={
            isQueued ? "Remove from queue" : isSkipped ? "Restore" : DRAWER_FOOTER_CONTENT.skipAccessibilityLabel
          }
          onPress={isQueued ? handleRemoveFromQueue : handleSkipRestore}
          style={styles.skipActionButton}>
          <XIcon size={15} color={colors.grey[700]} />
          <CustomText
            text={isQueued ? "Remove from Queue" : isSkipped ? "Restore" : DRAWER_FOOTER_CONTENT.skipLabel}
            useTranslationText={false}
            size="xs"
            weight="medium"
            style={styles.skipActionText}
          />
        </Pressable>
      </View>
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={openEditorLabel}
        style={(state) => [styles.openEditorButton, getTransparentButtonInteractionStyle(state)]}>
        <ArrowSquareOutIcon size={15} color={colors.grey[700]} />
        <CustomText
          text={DRAWER_FOOTER_CONTENT.openInEditorLabel}
          useTranslationText={false}
          size="xs"
          weight="medium"
          style={styles.openEditorText}
        />
      </Pressable>
    </View>
  );
}

function getTransparentButtonInteractionStyle({ hovered, pressed }: { hovered?: boolean; pressed: boolean }) {
  if (pressed) {
    return styles.transparentButtonPressed;
  }

  if (hovered) {
    return styles.transparentButtonHovered;
  }

  return undefined;
}

function FlagPill({
  pill,
  onShowGapAnalysisScorecard,
}: {
  pill: GapActionFlagPillData;
  onShowGapAnalysisScorecard: (dimensionId: string) => void;
}) {
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={pill.accessibilityLabel}
      onPress={() => onShowGapAnalysisScorecard(pill.dimensionId)}
      style={(state) => [styles.flagPill, getFlagPillInteractionStyle(state)]}>
      <CustomText text={pill.label} useTranslationText={false} size="xs" weight="medium" style={styles.flagPillText} />
    </Pressable>
  );
}

function getFlagPillInteractionStyle({ hovered, pressed }: { hovered?: boolean; pressed: boolean }) {
  if (pressed) {
    return styles.flagPillPressed;
  }

  if (hovered) {
    return styles.flagPillHovered;
  }

  return undefined;
}

function DiffRow({
  label,
  current,
  proposed,
  proposedMeta,
  isChanged = false,
}: {
  label: string;
  current: string;
  proposed: string;
  proposedMeta: string;
  isChanged?: boolean;
}) {
  return (
    <View style={styles.diffRow}>
      <View style={[styles.diffCell, styles.diffLeftBorder]}>
        <CustomText text={label} useTranslationText={false} size="xs" weight="bold" style={styles.diffLabel} />
        <CustomText text={current} useTranslationText={false} size="s" weight="normal" style={styles.diffBodyText} />
      </View>
      <View style={[styles.diffCell, isChanged && styles.changedCell]}>
        <View style={styles.diffMetaRow}>
          <CustomText text={label} useTranslationText={false} size="xs" weight="bold" style={styles.diffLabel} />
          <View style={isChanged ? styles.changedBadge : undefined}>
            <CustomText
              text={proposedMeta}
              useTranslationText={false}
              size="xs"
              weight="medium"
              style={isChanged ? styles.changedBadgeText : styles.noChangeText}
            />
          </View>
        </View>
        <CustomText
          text={proposed}
          useTranslationText={false}
          size="s"
          weight="normal"
          style={styles.proposedBodyText}
        />
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  drawerMotion: {
    backgroundColor: colors.white,
    borderLeftWidth: 1,
    borderLeftColor: colors.grey[200],
    width: "100%",
    maxWidth: 720,
    height: "100%",
    flexDirection: "column",
    shadowColor: colors.grey[900],
    shadowOpacity: 0.12,
    shadowRadius: 20,
    shadowOffset: { width: -8, height: 0 },
    zIndex: 1000,
    elevation: 24,
    ...(Platform.OS === "web"
      ? ({
          position: "fixed",
          top: 0,
          right: 0,
          bottom: 0,
          height: "100vh",
          display: "flex",
        } as unknown as ViewStyle)
      : {
          position: "absolute",
          top: 0,
          right: 0,
          bottom: 0,
        }),
  },
  drawerLayout: {
    borderWidth: 0,
    borderRadius: 0,
  },
  drawerBody: {
    flex: 1,
    minHeight: 0,
    paddingHorizontal: 30,
    paddingVertical: 26,
    gap: 20,
    overflow: "hidden",
    ...(Platform.OS === "web"
      ? ({
          overflowY: "auto",
          overflowX: "hidden",
        } as unknown as ViewStyle)
      : {}),
  },
  flaggedRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    flexWrap: "wrap",
  },
  flaggedLabel: {
    color: colors.grey[500],
    fontSize: 11,
    lineHeight: 16,
    textTransform: "uppercase",
    letterSpacing: 0.4,
  },
  flagPill: {
    minHeight: 24,
    justifyContent: "center",
    backgroundColor: "#FFF1E8",
    borderRadius: 7,
    paddingHorizontal: 8,
    paddingVertical: 3,
  },
  flagPillHovered: {
    backgroundColor: "#FFE4D1",
  },
  flagPillPressed: {
    backgroundColor: "#FED7AA",
    transform: [{ scale: 0.985 }],
  },
  flagPillText: {
    color: "#C2410C",
  },
  moduleBadge: {
    alignSelf: "flex-start",
    minHeight: 24,
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    backgroundColor: "#F3EAFE",
    borderRadius: 7,
    paddingHorizontal: 8,
    paddingVertical: 3,
  },
  moduleBadgeText: {
    color: "#7244BB",
  },
  actionTitle: {
    color: colors.grey[900],
  },
  reasonCard: {
    borderWidth: 1,
    borderColor: colors.grey[200],
    borderRadius: 16,
    padding: 20,
    gap: 10,
  },
  reasonTitle: {
    color: colors.grey[700],
  },
  reasonText: {
    color: colors.grey[700],
    lineHeight: 24,
  },
  diffCard: {
    borderWidth: 1,
    borderColor: colors.grey[200],
    borderRadius: 16,
    overflow: "hidden",
  },
  diffHeaderRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: colors.grey[200],
  },
  diffHeaderCell: {
    flex: 1,
    minHeight: 50,
    paddingHorizontal: 20,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  diffLeftBorder: {
    borderRightWidth: 1,
    borderRightColor: colors.grey[200],
  },
  diffHeaderText: {
    color: colors.grey[700],
  },
  diffProposedHeader: {
    backgroundColor: "#F1FBEA",
  },
  proposedHeaderText: {
    color: "#2B7A0B",
  },
  editButton: {
    flexDirection: "row",
    alignItems: "center",
    gap: 5,
    backgroundColor: colors.white,
    borderWidth: 1,
    borderColor: colors.grey[200],
    borderRadius: 7,
    paddingHorizontal: 11,
    paddingVertical: 7,
  },
  editButtonText: {
    color: colors.grey[700],
  },
  diffRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: colors.grey[100],
  },
  diffCell: {
    flex: 1,
    paddingHorizontal: 24,
    paddingVertical: 22,
    gap: 10,
  },
  diffMetaRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  diffLabel: {
    color: colors.grey[500],
    textTransform: "uppercase",
    letterSpacing: 0.4,
  },
  diffBodyText: {
    color: colors.grey[700],
    lineHeight: 24,
  },
  proposedBodyText: {
    color: colors.grey[900],
    lineHeight: 24,
  },
  noChangeText: {
    color: colors.grey[500],
  },
  changedCell: {
    backgroundColor: "rgba(243, 254, 231, 0.3)",
  },
  changedBadge: {
    backgroundColor: "#F3FEE7",
    borderRadius: 5,
    paddingHorizontal: 6,
    paddingVertical: 2,
  },
  changedBadgeText: {
    color: "#3B7C0F",
  },
  drawerFooter: {
    minHeight: 56,
    borderTopWidth: 1,
    borderTopColor: colors.grey[200],
    paddingHorizontal: 24,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    flexShrink: 0,
  },
  footerActions: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
  },
  queueButton: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    backgroundColor: colors.brand[500],
    borderRadius: 6,
    paddingHorizontal: 13,
    paddingVertical: 8,
  },
  queueButtonQueued: {
    backgroundColor: colors.success[600],
  },
  queueButtonText: {
    color: colors.white,
  },
  skipActionButton: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    paddingHorizontal: 10,
    paddingVertical: 8,
  },
  skipButtonSkipped: {
    backgroundColor: colors.grey[100],
    borderRadius: 6,
  },
  skipActionText: {
    color: colors.grey[700],
  },
  skipActionTextSkipped: {
    color: colors.grey[900],
  },
  openEditorButton: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    borderRadius: 4,
    paddingHorizontal: 10,
    paddingVertical: 8,
  },
  transparentButtonHovered: {
    backgroundColor: colors.grey[50],
    transform: [{ scale: 1.015 }],
  },
  transparentButtonPressed: {
    backgroundColor: colors.grey[100],
    transform: [{ scale: 0.985 }],
  },
  openEditorText: {
    color: colors.grey[700],
  },
});

export default GapActionDrawerSlideout;
